<template>
  <v-dialog v-model="dialog" max-width="1100" :fullscreen="$vuetify.breakpoint.smAndDown" persistent scrollable>
    <v-card>
      <v-card-title class="d-flex justify-space-between">
        <div class="text-subtitle-1">
          Please confirm
          <div class="text-caption font-weight-light">
            These reports sit on a lot their worksheet number or document disagrees with
            ({{ startDate }} to {{ endDate }}). Auto-assign left them for you.
          </div>
        </div>
        <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-divider />
      <v-card-text class="pt-3">
        <v-progress-linear v-if="loading" indeterminate class="mb-3" />
        <div v-else-if="loadFailed" class="text-body-2 my-4">
          Could not load the list.
          <v-btn small text color="primary" @click="load">Retry</v-btn>
        </div>
        <div v-else-if="rows.length === 0" class="text-body-2 my-4">Nothing left to confirm.</div>
        <v-card v-for="row in rows" :key="row.result.id" outlined class="mb-3 pa-3">
          <div class="d-flex flex-wrap align-center">
            <div class="flex-grow-1 mr-4">
              <div class="font-weight-medium">
                {{ row.result.sampleName }} · {{ row.result.averoutN }}
                <span class="text-caption grey--text ml-2">{{ row.result.testTime | formatDateTime }}</span>
              </div>
              <div class="text-caption orange--text text--darken-3">{{ row.result.lotCheck }}</div>
              <div class="text-caption">
                Now on lot {{ row.result.inventoryId }}
                <span v-if="row.suggested">
                  · suggested lot {{ row.suggested.inventoryId }}
                  <span v-if="row.suggested.documentNumber">({{ row.suggested.documentNumber }})</span>
                </span>
              </div>
            </div>
            <div class="d-flex flex-wrap">
              <v-btn small color="primary" class="ma-1" :disabled="!row.suggested || row.busy" :loading="row.busy === 'use'" @click="useSuggested(row)">Use suggested</v-btn>
              <v-btn small outlined class="ma-1" :disabled="!!row.busy" :loading="row.busy === 'keep'" @click="keepCurrent(row)">Keep current</v-btn>
              <v-btn small outlined class="ma-1" :disabled="!!row.busy" @click="chooseOther(row)">Choose another…</v-btn>
              <v-btn small text class="ma-1" :disabled="!!row.busy" :loading="row.busy === 'none'" @click="leaveUnassigned(row)">Leave unassigned</v-btn>
            </div>
          </div>
        </v-card>
      </v-card-text>
      <v-card-actions>
        <span class="text-caption grey--text ml-2" v-if="total > 0">
          {{ remaining }} to confirm<span v-if="total > loaded"> · showing {{ loaded }} at a time</span>
        </span>
        <v-spacer />
        <v-btn text @click="close">Later</v-btn>
      </v-card-actions>
    </v-card>
    <lab-result-lot-chooser
      :activated.sync="chooserOpen"
      :lab-result="chooserRow ? chooserRow.result : null"
      @chosen="(id) => decided(chooserRow, id, 'use')"
      @unassign="decided(chooserRow, null, 'none')"
    />
  </v-dialog>
</template>

<script>
import { mapActions } from "vuex";
import { decideLot, keepLot, alreadyReported } from "./labLotActions";
const LabResultLotChooser = () => import("./LabResultLotChooser.vue");

const PAGE_SIZE = 50;

async function inBatches(items, size, work) {
  for (let i = 0; i < items.length; i += size) {
    await Promise.all(items.slice(i, i + size).map(work));
  }
}

// After auto-assign: the reports it would not decide alone (their lot disagrees with their
// worksheet number or document), each with the rules' suggestion, for a person to confirm.
export default {
  components: { LabResultLotChooser },
  props: {
    activated: { type: Boolean, default: false },
    startDate: { type: String, default: null },
    endDate: { type: String, default: null },
  },
  data: () => ({
    dialog: false,
    loading: false,
    rows: [],
    chooserOpen: false,
    chooserRow: null,
    total: 0,
    loaded: 0,
    loadFailed: false,
    loadSeq: 0,
  }),
  computed: {
    // Still to confirm in the range: those listed now plus those not loaded yet.
    remaining() {
      return this.rows.length + Math.max(0, this.total - this.loaded);
    },
  },
  watch: {
    activated(open) {
      this.dialog = open;
      if (open) this.load();
    },
  },
  methods: {
    ...mapActions("messages", ["addErrorMessage"]),
    async load() {
      const seq = ++this.loadSeq;
      this.rows = [];
      this.total = 0;
      this.loaded = 0;
      this.loadFailed = false;
      this.loading = true;
      try {
        const page = (await this.$axios.get("/inventory-lab-result", {
          params: { flaggedOnly: true, startTime: this.startDate, endTime: this.endDate, page: 0, size: PAGE_SIZE, sort: "testTime,desc" },
        })).data;
        if (seq !== this.loadSeq) return;
        const flagged = (page && page.content) || [];
        const rows = flagged.map((result) => ({ result, suggested: null, busy: null }));
        this.total = (page && page.totalElements) || flagged.length;
        this.loaded = rows.length;
        this.rows = rows;
        // The suggestion is the rules' pick among the report's candidate lots; a few at a time,
        // so a long list neither floods the server nor times out waiting in the browser.
        await inBatches(rows, 6, async (row) => {
          try {
            const candidates = (await this.$axios.get(`/inventory-lab-result/${row.result.id}/lot-candidates`)).data || [];
            row.suggested = candidates.find((c) => c.suggested && !c.current) || null;
          } catch (e) {
            row.suggested = null;
          }
        });
      } catch (e) {
        if (seq === this.loadSeq) this.loadFailed = true;
      } finally {
        if (seq === this.loadSeq) this.loading = false;
      }
    },
    useSuggested(row) {
      return row.suggested ? this.decided(row, row.suggested.inventoryId, "use") : Promise.resolve();
    },
    leaveUnassigned(row) {
      return this.decided(row, null, "none");
    },
    chooseOther(row) {
      this.chooserRow = row;
      this.chooserOpen = true;
    },
    async keepCurrent(row) {
      await this.save(row, "keep", () => keepLot(this.$axios, row.result.id));
    },
    async decided(row, inventoryId, how) {
      if (!row) return;
      await this.save(row, how, () => decideLot(this.$axios, row.result.id, inventoryId));
    },
    async save(row, how, action) {
      row.busy = how;
      try {
        await action();
        this.rows = this.rows.filter((r) => r !== row);
        this.$emit("changed");
        // This batch is done and more are waiting: fetch the next ones.
        if (this.rows.length === 0 && this.total > this.loaded) this.load();
      } catch (error) {
        if (!alreadyReported(error)) this.addErrorMessage("Could not save the lot.");
      } finally {
        row.busy = null;
      }
    },
    close() {
      this.loadSeq++;
      this.dialog = false;
      this.$emit("update:activated", false);
    },
  },
};
</script>
